import { getBtcBalance } from '@optimex-xyz/trade-wallet-sdk'

import { API_ENDPOINTS, SUPPORTED_NETWORK } from '@/utils'

import axios from 'axios'

interface ExplorerBalanceResponse {
  chain_stats: {
    funded_txo_sum: number
    spent_txo_sum: number
  }

  [key: string]: any
}

const API_TIMEOUT = 5000

class BitcoinBalanceHelper {
  constructor(
    private readonly networkId: string,
    private readonly address: string
  ) {}

  async getBalance(): Promise<number | string | bigint> {
    try {
      const mempoolBalance = await this.getBalanceFromMempool()

      if (mempoolBalance !== null) {
        return mempoolBalance
      }

      const blockstreamBalance = await this.getBalanceFromBlockstream()
      if (blockstreamBalance !== null) {
        return blockstreamBalance
      }

      const sdkBalance = await this.getBalanceWithTimeout(async () => {
        try {
          return await getBtcBalance(this.networkId, this.address)
        } catch {
          return null
        }
      })
      return sdkBalance || 0
    } catch {}
    return 0
  }

  private async getBalanceWithTimeout<T>(balanceFn: () => Promise<T | null>): Promise<T | null> {
    return new Promise<T | null>(async (resolve) => {
      const timeoutId = setTimeout(() => {
        resolve(null)
      }, API_TIMEOUT)

      try {
        const result = await balanceFn()
        clearTimeout(timeoutId)
        resolve(result)
      } catch {
        clearTimeout(timeoutId)
        resolve(null)
      }
    })
  }

  private async getBalanceFromMempool(): Promise<number | null> {
    try {
      const network = this.networkId === SUPPORTED_NETWORK.BTC ? 'mainnet' : 'testnet'
      const baseUrl = API_ENDPOINTS.mempool[network]

      const response = await axios.get<ExplorerBalanceResponse>(`${baseUrl}/address/${this.address}`, {
        timeout: API_TIMEOUT,
      })

      if (response?.data) {
        const confirmedBalance =
          response.data.chain_stats?.funded_txo_sum - response.data.chain_stats?.spent_txo_sum || 0

        // const unconfirmedBalance =
        //   response.data.mempool_stats?.funded_txo_sum - response.data.mempool_stats?.spent_txo_sum || 0

        return Math.max(0, confirmedBalance)
      }
    } catch {}

    return null
  }

  private async getBalanceFromBlockstream(): Promise<number | null> {
    try {
      const network = this.networkId === SUPPORTED_NETWORK.BTC ? 'mainnet' : 'testnet'
      const baseUrl = API_ENDPOINTS.blockstream[network]

      const response = await axios.get<ExplorerBalanceResponse>(`${baseUrl}/address/${this.address}`, {
        timeout: API_TIMEOUT,
      })

      if (response?.data) {
        const confirmedBalance =
          response.data.chain_stats?.funded_txo_sum - response.data.chain_stats?.spent_txo_sum || 0

        // const unconfirmedBalance =
        //   response.data.mempool_stats?.funded_txo_sum - response.data.mempool_stats?.spent_txo_sum || 0

        return Math.max(0, confirmedBalance)
      }
    } catch {}

    return null
  }
}

export async function getBitcoinBalance(networkId: string, address: string): Promise<number | string | bigint> {
  const helper = new BitcoinBalanceHelper(networkId, address)
  return helper.getBalance()
}
